import { useInView } from "../../hooks/useInView";

interface Step {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface ProcessSectionProps {
  steps?: Step[];
  heading?: string;
}

const defaultSteps: Step[] = [
  {
    number: "01",
    title: "Free Consultation",
    description:
      "We meet at your property to understand your vision, lifestyle, and goals. No pressure \u2014 just a genuine conversation about what\u2019s possible for your outdoor space.",
    icon: "ri-calendar-check-fill",
  },
  {
    number: "02",
    title: "Custom Design Proposal",
    description:
      "Our designers create a detailed project plan tailored to your property\u2019s dimensions, style, and budget. You\u2019ll see the vision before a single stone is placed.",
    icon: "ri-draft-fill",
  },
  {
    number: "03",
    title: "Material Selection",
    description:
      "We guide you through premium material options \u2014 natural stone, synthetic turf, water feature elements \u2014 ensuring every choice reflects the quality you expect.",
    icon: "ri-stack-fill",
  },
  {
    number: "04",
    title: "Expert Installation",
    description:
      "Our artisan crews handle every phase of construction in-house. No subcontracting. No shortcuts. Precise, professional installation with clean jobsite management.",
    icon: "ri-hammer-fill",
  },
];

export default function ProcessSection({
  steps = defaultSteps,
  heading = "From Vision to Reality \u2014 Our Proven Process",
}: ProcessSectionProps) {
  const { ref, inView } = useInView(0.08);

  return (
    <section ref={ref} className="bg-[#111111] py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 md:px-8 lg:px-16">

        {/* Header */}
        <div className={`max-w-2xl mb-16 reveal ${inView ? "visible" : ""}`}>
          <span className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-rust-400 mb-3 block">
            How It Works
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {heading}
          </h2>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 lg:divide-x divide-stone-800">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`relative pt-10 pr-0 lg:pr-10 pb-10 pl-0 lg:pl-6 first:pl-0 reveal ${inView ? "visible" : ""} reveal-delay-${i + 1}`}
            >
              {/* Giant background number */}
              <div className="font-serif text-[120px] md:text-[140px] font-bold text-stone-800 leading-none select-none absolute top-0 left-0 lg:left-4 -z-0 pointer-events-none">
                {step.number}
              </div>

              {/* Content over the number */}
              <div className="relative z-10 pt-16">
                {/* Icon */}
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-rust-700/20 border border-rust-700/30 mb-5">
                  <i className={`${step.icon} text-rust-400 text-base`}></i>
                </div>

                <h3 className="font-sans text-base font-bold text-white mb-3 leading-snug">
                  {step.title}
                </h3>
                <p className="font-sans text-sm text-stone-400 leading-relaxed">
                  {step.description}
                </p>

                {/* Connector dot */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex items-center gap-1 mt-6">
                    <div className="w-1.5 h-1.5 rounded-full bg-rust-700"></div>
                    <div className="flex-1 h-px bg-stone-800"></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className={`mt-14 pt-12 border-t border-stone-800 flex flex-col sm:flex-row items-center justify-between gap-5 reveal ${inView ? "visible" : ""} reveal-delay-5`}>
          <p className="font-sans text-sm text-stone-400 text-center sm:text-left">
            Every project starts with a <strong className="text-white">free, no-obligation consultation.</strong>
          </p>
          <a
            href="/#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="whitespace-nowrap inline-flex items-center gap-2 bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-7 py-3 rounded-sm transition-colors cursor-pointer"
          >
            <i className="ri-calendar-check-fill"></i>
            Book Your Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
